import { type Replacement, updateUrl } from "@imprivata-cloud/common";
import { securityHeaders } from "./client";

export const CommonHeaders = {
	"Content-Type": "application/json",
	...securityHeaders,
};

export const Endpoints = {
	TENANT_LOOKUP_BY_DOMAIN: "/idp-web/tenant-lookup/v1/tenants-by-domain/get",
	AUTH_ACTION_DETERMINE: "/idp-web-authn/v1/authn-action/determine",
	CREATE_CLIENT_USER_SESSION: "/idp-web-authn/v1/client-user-session/create",
};

export interface RequestInitImpr extends RequestInit {
	workflowId?: string | null;
}

/// Shared fetch wrapper
export const fetcher = async (
	endpoint: (typeof Endpoints)[keyof typeof Endpoints] | string,
	{ workflowId, ...options }: RequestInitImpr,
	tenantId?: string,
) => {
	const tenantIdParam = tenantId
		? tenantId
		: new URLSearchParams(window.location.search).get("tenantId") || "";

	const apiUrl = getApiUrl(tenantIdParam);
	const apiBase = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
	const endpointPath = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

	return fetch(`${apiBase}${endpointPath}`, {
		...options,
		credentials: "include",
		headers: {
			// todo: we need client name
			...CommonHeaders,
			...(options.headers || {}),
			...(workflowId ? { "Impr-Workflow-Id": workflowId } : {}),
		},
	})
		.then(async (response) => {
			if (response.ok) {
				return response
					.json()
					.then((json) => json)
					.catch(() => null); //response with empty body
			}
			const error = await response.json().then((json) => json);
			return Promise.reject(error.error);
		})
		.catch((error) => {
			console.error("Error fetching data:", endpoint, error);
			return Promise.reject(error);
		});
};

// Interpolate the API URL based on the current environment
export function getApiUrl(tenantId?: string): string {
	const override = import.meta.env.DEV
		? (import.meta.env.VITE_API_URL as string | undefined)
		: undefined;
	if (override) {
		return resolveOverrideApiUrl(override, tenantId);
	}

	const host = tenantId ? tenantId : "astra";
	const urls = _getUrl({ apiUrl: { sub: `${host}.sys` } });
	return sanitizeApiUrl(urls?.apiUrl);
}

export function resolveOverrideApiUrl(override: string, tenantId?: string): string {
	if (!override) {
		return "";
	}

	if (tenantId && override.includes("{tenantId}")) {
		return sanitizeApiUrl(override.replaceAll("{tenantId}", tenantId.toLowerCase()));
	}

	try {
		const parsed = new URL(override);
		const isLocalMockHost = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
		if (tenantId && !isLocalMockHost && parsed.hostname.includes(".sys.")) {
			const hostnameParts = parsed.hostname.split(".");
			hostnameParts[0] = tenantId.toLowerCase();
			parsed.hostname = hostnameParts.join(".");
		}
		return sanitizeApiUrl(parsed.toString());
	} catch {
		return sanitizeApiUrl(override);
	}
}

// Get the current environment and top-level domain from the current host
export function _getUrl(urlToBeGenerated: Record<string, Replacement>) {
	const includePort = import.meta.env.VITE_INCLUDE_PORT === "true";
	const port = window.location.port;
	const url = window.location.hostname;
	const customRegex = /^(?:https?:\/\/)?([A-Za-z0-9_]+)\w+/;
	const urlRegex = url?.includes(".app") ? undefined : customRegex;

	if (url?.includes("localhost")) {
		return addPortIfNeeded(
			{
				apiUrl: "http://localhost:8080",
				idpUrl: `https://${urlToBeGenerated.idpUrl?.sub}.dev.imprivata.cloud`,
				portalUrl: `https://${urlToBeGenerated.portalUrl?.sub}.dev.imprivata.cloud`,
			},
			includePort,
			port,
		);
	}

	return addPortIfNeeded(
		updateUrl(url, urlToBeGenerated, "https://", urlRegex),
		includePort,
		port,
	);
}

function addPortIfNeeded(
	urls: Record<string, string>,
	includePort: boolean,
	port: string,
): Record<string, string> {
	if (!includePort || !port) {
		return urls;
	}

	return Object.entries(urls).reduce<Record<string, string>>(
		(acc, [key, value]) => {
			if (!value) {
				acc[key] = value;
				return acc;
			}
			try {
				const url = new URL(value);
				if (!url.port) {
					url.port = port;
				}
				acc[key] = url.toString();
			} catch {
				acc[key] = value;
			}
			return acc;
		},
		{},
	);
}

function sanitizeApiUrl(value?: string): string {
	if (!value) {
		return "";
	}

	try {
		const parsed = new URL(value);
		if (!parsed.port) {
			return value;
		}

		const isLocalMockHost = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
		if (isLocalMockHost) {
			return value;
		}

		const portSuffix = `:${parsed.port}`;
		return value.includes(portSuffix) ? value.replace(portSuffix, "") : value;
	} catch {
		return value;
	}
}